import { Link, createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Clock,
  FileJson,
  Globe,
  Smartphone,
  User,
  XCircle,
} from 'lucide-react'
import { LoadingSpinner } from '@/components/loading-spinner'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useAuditLog } from '@/lib/api'
import { localizeDigits } from '@/lib/localize-digits'

export const Route = createFileRoute('/settings/audit-logs/$logId')({
  component: AuditLogDetailPage,
})

function AuditLogDetailPage() {
  const { logId } = Route.useParams()
  const { t } = useTranslation()
  const { data: log, isLoading, error } = useAuditLog(parseInt(logId, 10))

  const getStatusIcon = (statusCode: number) => {
    if (statusCode >= 200 && statusCode < 300) {
      return <CheckCircle className="h-5 w-5 text-green-500" />
    } else if (statusCode >= 400 && statusCode < 500) {
      return <AlertCircle className="h-5 w-5 text-orange-500" />
    } else if (statusCode >= 500) {
      return <XCircle className="h-5 w-5 text-red-500" />
    }
    return <AlertCircle className="h-5 w-5 text-muted-foreground" />
  }

  const getStatusColorClass = (statusCode: number) => {
    if (statusCode >= 200 && statusCode < 300) {
      return 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20'
    } else if (statusCode >= 400 && statusCode < 500) {
      return 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20'
    } else if (statusCode >= 500) {
      return 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20'
    }
    return 'bg-muted text-muted-foreground'
  }

  const getStatusText = (statusCode: number) => {
    const statusTexts: Record<number, string> = {
      200: 'OK',
      201: 'Created',
      204: 'No Content',
      400: 'Bad Request',
      401: 'Unauthorized',
      403: 'Forbidden',
      404: 'Not Found',
      422: 'Unprocessable Entity',
      500: 'Internal Server Error',
      502: 'Bad Gateway',
      503: 'Service Unavailable',
    }
    return statusTexts[statusCode] || 'Unknown'
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return localizeDigits(
      date.toLocaleString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
    )
  }

  const formatDuration = (ms: number) => {
    if (ms < 1000) {
      return localizeDigits(`${ms.toFixed(0)} ms`)
    } else if (ms < 60000) {
      return localizeDigits(`${(ms / 1000).toFixed(2)} s`)
    } else {
      const minutes = Math.floor(ms / 60000)
      const seconds = ((ms % 60000) / 1000).toFixed(2)
      return localizeDigits(`${minutes} min ${seconds} s`)
    }
  }

  const formatJson = (data: unknown) => {
    if (!data) return null
    try {
      return JSON.stringify(data, null, 2)
    } catch {
      return String(data)
    }
  }

  if (isLoading) {
    return (
      <div>
        <div className="fixed-header">
          <div className="container mx-auto max-w-screen-md px-4 py-3">
            <div className="flex items-center gap-3">
              <Link to="/settings/audit-logs">
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-bold">
                  {t('auditLogs.detail.title')}
                </h1>
              </div>
            </div>
          </div>
        </div>
        <div className="container mx-auto max-w-screen-md px-4 content-with-fixed-header pb-nav-and-safe-area">
          <div className="space-y-4 pt-4">
            <Card>
              <CardContent className="py-12">
                <LoadingSpinner label={t('auditLogs.detail.loading')} />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  if (error || !log) {
    return (
      <div>
        <div className="fixed-header">
          <div className="container mx-auto max-w-screen-md px-4 py-3">
            <div className="flex items-center gap-3">
              <Link to="/settings/audit-logs">
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-bold">
                  {t('auditLogs.detail.title')}
                </h1>
              </div>
            </div>
          </div>
        </div>
        <div className="container mx-auto max-w-screen-md px-4 content-with-fixed-header pb-nav-and-safe-area">
          <div className="space-y-4 pt-4">
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3 text-center">
                  <AlertCircle className="h-12 w-12 text-destructive" />
                  <div>
                    <p className="font-medium text-destructive">
                      {t('auditLogs.detail.error')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {error instanceof Error
                        ? error.message
                        : t('auditLogs.detail.notFound')}
                    </p>
                  </div>
                  <Link to="/settings/audit-logs">
                    <Button variant="outline" size="sm">
                      <ArrowLeft className="h-4 w-4 me-2" />
                      {t('auditLogs.detail.backToList')}
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Fixed Header */}
      <div className="fixed-header">
        <div className="container mx-auto max-w-screen-md px-4 py-3">
          <div className="flex items-center gap-3">
            <Link to="/settings/audit-logs">
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold">
                {t('auditLogs.detail.title')}
              </h1>
              <p className="text-xs text-muted-foreground truncate">
                {t('auditLogs.detail.logId')}: {localizeDigits(log.id)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="container mx-auto max-w-screen-md px-4 content-with-fixed-header pb-nav-and-safe-area">
        <div className="space-y-4 pt-4">
          {/* Status Overview Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-3">
                  {getStatusIcon(log.response_status)}
                  <span>{t('auditLogs.detail.overview')}</span>
                </CardTitle>
                <Badge
                  variant="outline"
                  className={getStatusColorClass(log.response_status)}
                >
                  {localizeDigits(log.response_status)}{' '}
                  {getStatusText(log.response_status)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Request Info */}
              <div>
                <p className="text-sm font-medium mb-2">
                  {t('auditLogs.detail.request')}
                </p>
                <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <code className="text-sm flex-1 break-all">
                      {log.action}
                    </code>
                  </div>
                </div>
              </div>

              {/* Timing Info */}
              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    {t('auditLogs.detail.duration')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDuration(log.duration_ms ?? 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* User & Context Info */}
          <Card>
            <CardHeader>
              <CardTitle>{t('auditLogs.detail.context')}</CardTitle>
              <CardDescription>
                {t('auditLogs.detail.contextDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* User Account */}
              <div className="flex items-start gap-3">
                <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    {t('auditLogs.detail.userAccount')}
                  </p>
                  <p className="text-sm text-muted-foreground break-all">
                    {log.user_account}
                  </p>
                </div>
              </div>

              {/* Timestamp */}
              <div className="flex items-start gap-3">
                <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    {t('auditLogs.detail.timestamp')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(log.timestamp)}
                  </p>
                </div>
              </div>

              {/* IP Address */}
              {log.client_ip && (
                <div className="flex items-start gap-3">
                  <Globe className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {t('auditLogs.detail.ipAddress')}
                    </p>
                    <p className="text-sm text-muted-foreground font-mono">
                      {log.client_ip}
                    </p>
                  </div>
                </div>
              )}

              {/* User Agent */}
              {log.user_agent && (
                <div className="flex items-start gap-3">
                  <Smartphone className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {t('auditLogs.detail.userAgent')}
                    </p>
                    <p className="text-xs text-muted-foreground break-all">
                      {log.user_agent}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Request Body */}
          {log.request_body && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileJson className="h-5 w-5" />
                  {t('auditLogs.detail.requestBody')}
                </CardTitle>
                <CardDescription>
                  {t('auditLogs.detail.requestBodyDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted/50 rounded-lg p-3 overflow-x-auto text-xs">
                  <code>{formatJson(log.request_body)}</code>
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Response Body */}
          {log.response_body && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileJson className="h-5 w-5" />
                  {t('auditLogs.detail.responseBody')}
                </CardTitle>
                <CardDescription>
                  {t('auditLogs.detail.responseBodyDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted/50 rounded-lg p-3 overflow-x-auto text-xs">
                  <code>{formatJson(log.response_body)}</code>
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Query Parameters */}
          {log.query_params && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileJson className="h-5 w-5" />
                  {t('auditLogs.detail.queryParams')}
                </CardTitle>
                <CardDescription>
                  {t('auditLogs.detail.queryParamsDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted/50 rounded-lg p-3 overflow-x-auto text-xs">
                  <code>{formatJson(log.query_params)}</code>
                </pre>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
